import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import jwt from "jwt-decode";
import { createStructuredSelector } from "reselect";
import { Layout, Breadcrumb, Modal } from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import LanguageSelector from "../LanguageSelector";
import classes from "./style.css";
import logoText from "assets/images/icon.png";
import logo from "assets/images/icon.png";
import Background from "assets/images/menu.png";
import { makeSelectLocale } from "../../translations/language-provider/selectors";
import UserMenu from "../../components/DropdownUserMenu";
import { updatePassword } from "lib/providers/auth-provider/actions";
import {
  makeSelectToken,
  makeSelectRole,
  makeSelectIsAuth,
} from "lib/providers/auth-provider/selectors";
import { Redirect } from "react-router-dom";
import AdminMenu from "./menus/adminMenu";
import ConsultantMenu from "./menus/consultantMenu";
import links from "./menus/links";
import GlobalSearch from "./GlobalSearch";
import { useUser } from "../../contexts/user-context";
import { canAccessAdminPath } from "../../utils/accessContext";

const { Header, Sider, Content } = Layout;

const MasterPageLayout = ({
  breadcrumb = [],
  level,
  name,
  role,
  token,
  keys,
  logout,
  intl,
  history,
  children,
  additionalClasses,
}) => {
  const translate = (id) => intl.formatMessage({ id });

  const [collapsed, setCollapsed] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (token) {
      setUserId(jwt(token).sub);
    }
  }, [token]);

  const toggle = () => {
    setCollapsed((prev) => !prev);
  };

  const menuProps = {
    key: "Menu",
    userId: userId,
    links: links({ collapsed, userId }, translate),
    translate: translate,
    role,
    ...keys,
  };

  const { user, userLoading } = useUser();
  const isAdmin = role === "admin";
  const isAssistant = role === "assistant";
  const usesAdminChrome = isAdmin || isAssistant;

  if (isAssistant && !userLoading) {
    if (!canAccessAdminPath(user, role, history.location.pathname)) {
      return <Redirect to="/admin/welcome" />;
    }
  }

  return (
    <Layout
      key={"admin-page"}
      className={`${classes.layout} ${additionalClasses || ""}`}
    >
      <Sider
        className={classes.sider}
        style={{
          backgroundImage: `url(${Background})`,
        }}
        width={230}
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        collapsedWidth={isMobile ? 0 : 80}
        zeroWidthTriggerStyle={{ display: "none" }}
        onBreakpoint={(broken) => {
          setIsMobile(broken);
          setCollapsed(broken);
        }}
      >
        <div className={classes.logo}>
          <img alt="sensor logo" src={collapsed ? logo : logoText} />
        </div>
        {usesAdminChrome ? (
          <AdminMenu {...menuProps} />
        ) : (
          <ConsultantMenu {...menuProps} />
        )}
      </Sider>
      {isMobile && !collapsed && (
        <div className={classes.siderMask} onClick={() => setCollapsed(true)} />
      )}
      <Layout>
        <Header className={classes.header}>
          {collapsed ? (
            <MenuUnfoldOutlined
              className="trigger"
              style={{ verticalAlign: "unset", lineHeight: 0 }}
              onClick={toggle}
            />
          ) : (
            <MenuFoldOutlined
              className="trigger"
              style={{ verticalAlign: "unset", lineHeight: 0 }}
              onClick={toggle}
            />
          )}
          <div className={classes.left}>
            {isAdmin && !isMobile && !isAssistant && (
              <div className={classes.searchWrap}>
                <GlobalSearch userId={userId} />
              </div>
            )}
            <Breadcrumb className={classes.breadcrumb}>
              {usesAdminChrome && (
                <Breadcrumb.Item>
                  <HomeOutlined
                    className={classes.homeIcon}
                    onClick={() => history.push("/admin/welcome")}
                  />
                </Breadcrumb.Item>
              )}
              {level ? (
                <Breadcrumb.Item>{translate(level)}</Breadcrumb.Item>
              ) : null}
              {breadcrumb.map((path) => (
                <Breadcrumb.Item key={path}>
                  {links({ collapsed, userId }, translate)[path]}
                </Breadcrumb.Item>
              ))}
              {name && (
                <Breadcrumb.Item className={classes.breadcrumbName}>
                  {translate(name)}
                </Breadcrumb.Item>
              )}
            </Breadcrumb>
          </div>
          <div className={classes.right}>
            {isAdmin && isMobile && !isAssistant && (
              <SearchOutlined
                className={classes.mobileSearchIcon}
                onClick={() => setSearchOpen(true)}
              />
            )}
            <div className={classes.languageSelector}>
              <LanguageSelector />
            </div>
            <span className={classes.userMenuWrap}>
              <UserMenu userId={userId} token={token} logout={logout} />
            </span>
          </div>
        </Header>
        <Content className={classes.content}>{children}</Content>
      </Layout>

      {isAdmin && !isAssistant && (
        <Modal
          visible={searchOpen}
          onCancel={() => setSearchOpen(false)}
          footer={null}
          closable={true}
          destroyOnClose
          title={translate("search_placeholder")}
          width="92%"
          style={{ top: 40 }}
          bodyStyle={{ paddingTop: 8 }}
        >
          <GlobalSearch
            userId={userId}
            autoFocus
            className={classes.mobileSearchInput}
            onNavigate={() => setSearchOpen(false)}
          />
        </Modal>
      )}
    </Layout>
  );
};

const mapStateToProps = createStructuredSelector({
  locale: makeSelectLocale(),
  token: makeSelectToken(),
  role: makeSelectRole(),
  auth: makeSelectIsAuth(),
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      updatePassword,
    },
    dispatch,
  );
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default withConnect(withRouter(injectIntl(MasterPageLayout)));
